import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Button } from 'ui/Button';
import { EmptyState } from 'modules/Gateways/EmptyState';
import { GatewayFormModal, GatewayFormMode } from 'modules/Gateways/GatewayFormModal';
import { StatsCards } from 'modules/Gateways/StatsCards';
import { Toolbar } from 'modules/Gateways/Toolbar';
import { Filters } from 'modules/Gateways/Filters';
import { GatewaysTable } from 'modules/Gateways/GatewaysTable';

import { ModalType } from 'entities/common';
import { showModal } from 'store/Modal/Modal.action';
import { showToast } from 'store/Toast/Toast.action';

import {
  GatewayFilterObject,
  GatewayFilterUser,
  GatewayListItem,
  gatewaysApi,
} from 'services/gateways';

import styles from 'assets/scss/modules/Gateways/Gateways.module.scss';

export type GatewayStatus = 'online' | 'offline' | 'never_seen';

export interface Gateway {
  id: number;
  name: string;
  serial: string;
  model: string;
  freqBand: string;
  userId: number;
  user: string | null;
  objectId: number | null;
  object: string | null;
  status: GatewayStatus;
  lastSeen: string | null;
  devicesCount: number;
}

export interface FiltersState {
  user: string;
  object: string;
  status: string;
  model: string;
}

const PAGE_SIZE = 10;

function mapListItem(item: GatewayListItem): Gateway {
  return {
    id: item.id,
    name: item.name,
    serial: item.eui,
    model: item.model,
    freqBand: item.freq_band,
    userId: item.user_id,
    user: item.user_fullname,
    objectId: item.object_id,
    object: item.object_name,
    status: item.status,
    lastSeen: item.last_seen_at,
    devicesCount: item.devices_count,
  };
}

function applyFilters(
  items: Gateway[],
  search: string,
  filters: FiltersState,
): Gateway[] {
  const needle = search.trim().toLowerCase();
  return items.filter((g) => {
    if (needle) {
      const haystack = [g.name, g.serial, g.model, g.freqBand, g.user ?? '', g.object ?? '']
        .join(' ')
        .toLowerCase();
      if (!haystack.includes(needle)) return false;
    }
    if (filters.user && String(g.userId) !== filters.user) return false;
    if (filters.object && String(g.objectId) !== filters.object) return false;
    if (filters.status && g.status !== filters.status) return false;
    if (filters.model && g.model !== filters.model) return false;
    return true;
  });
}

function computeStats(items: Gateway[]) {
  return {
    total: items.length,
    online: items.filter((g) => g.status === 'online').length,
    offline: items.filter((g) => g.status === 'offline').length,
    neverSeen: items.filter((g) => g.status === 'never_seen').length,
  };
}

export const Gateways: React.FC = () => {
  const dispatch = useDispatch();
  const [gateways, setGateways] = useState<Gateway[]>([]);
  const [filterUsers, setFilterUsers] = useState<GatewayFilterUser[]>([]);
  const [filterObjects, setFilterObjects] = useState<GatewayFilterObject[]>([]);
  const [search, setSearch] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState<FiltersState>({
    user: '',
    object: '',
    status: '',
    model: '',
  });
  const [page, setPage] = useState(1);
  const [modalMode, setModalMode] = useState<GatewayFormMode | null>(null);
  const [editingGateway, setEditingGateway] = useState<Gateway | null>(null);

  const loadGateways = useCallback(
    async (silent = false) => {
      try {
        const res = await gatewaysApi.list();
        setGateways(res.items.map(mapListItem));
      } catch {
        if (!silent) {
          dispatch(showToast('Failed to load gateways. Please try again later.', 'error'));
        }
      }
    },
    [dispatch],
  );

  useEffect(() => {
    loadGateways();
  }, [loadGateways]);

  // FR-88 п.3: таблица обновляется автоматически без перезагрузки страницы,
  // в т.ч. при изменении статуса шлюза или его last seen на стороне сервера.
  useEffect(() => {
    const interval = setInterval(() => loadGateways(true), 30000);
    return () => clearInterval(interval);
  }, [loadGateways]);

  useEffect(() => {
    let cancelled = false;
    gatewaysApi
      .listFilterUsers()
      .then((res) => {
        if (!cancelled) setFilterUsers(res);
      })
      .catch(() => {});
    gatewaysApi
      .listFilterObjects()
      .then((res) => {
        if (!cancelled) setFilterObjects(res);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const stats = useMemo(() => computeStats(gateways), [gateways]);
  const filtered = useMemo(
    () => applyFilters(gateways, search, filters),
    [gateways, search, filters],
  );
  const isEmpty = gateways.length === 0;
  const isNoResults = !isEmpty && filtered.length === 0;
  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = useMemo(
    () => filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filtered, page],
  );

  const handleAdd = () => {
    setEditingGateway(null);
    setModalMode('create');
  };

  const handleEdit = (g: Gateway) => {
    setEditingGateway(g);
    setModalMode('edit');
  };

  const handleDelete = (g: Gateway) => {
    dispatch(
      showModal({
        modalType: ModalType.GATEWAY_DELETE,
        modalProps: {
          gatewayId: g.id,
          gatewayName: g.name,
          devicesCount: g.devicesCount,
          // FR-90 4d: после удаления обновляем таблицу и блок статистики
          // (stats пересчитывается из gateways).
          onDeleted: () => loadGateways(),
        },
      }),
    );
  };

  const openConfigModal = (
    gatewayName: string,
    eui: string,
    freqBand: string,
    registered: boolean,
  ) => {
    dispatch(
      showModal({
        modalType: ModalType.GATEWAY_CONFIG,
        modalProps: { gatewayName, eui, freqBand, registered },
      }),
    );
  };

  const closeModal = () => {
    setModalMode(null);
    setEditingGateway(null);
  };

  return (
    <div className={styles.page}>
      <section className={styles.card}>
        <header className={styles.cardHeader}>
          <h1 className={styles.title}>Gateway management</h1>
          <p className={styles.subtitle}>
            Monitor and manage all IoT gateways across your system
          </p>
          <Button
            size="small"
            variant="secondary"
            className={styles.addButton}
            onClick={handleAdd}
          >
            Add gateway
          </Button>
        </header>

        {!isEmpty && <StatsCards value={stats} />}
        <Toolbar
          search={search}
          filtersOpen={filtersOpen}
          onSearchChange={(v) => {
            setSearch(v);
            setPage(1);
          }}
          onToggleFilters={() => setFiltersOpen((open) => !open)}
        />
        {filtersOpen && !isEmpty && (
          <Filters
            values={filters}
            users={filterUsers}
            objects={filterObjects}
            onChange={(next) => {
              setFilters(next);
              setPage(1);
            }}
            onClear={() => {
              setFilters({ user: '', object: '', status: '', model: '' });
              setPage(1);
            }}
          />
        )}

        {isEmpty ? (
          <EmptyState variant="empty" onAdd={handleAdd} />
        ) : isNoResults ? (
          <EmptyState variant="no-results" />
        ) : (
          <GatewaysTable
            items={paged}
            page={page}
            pages={pages}
            onPageChange={setPage}
            onView={(g) => openConfigModal(g.name, g.serial, g.freqBand, false)}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        )}
      </section>

      <GatewayFormModal
        mode={modalMode}
        gateway={editingGateway}
        onClose={closeModal}
        onSuccess={loadGateways}
        onCreated={(created) =>
          openConfigModal(created.name, created.eui, created.freq_band, true)
        }
      />
    </div>
  );
};
